import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    PROCESS_TEMPLATE_NAME_MAX_LENGTH,
    type LeadType,
    type ObjectType,
    type ProcessDiagramPositionUpdateType,
    type ProcessRunTaskType,
    type ProcessRunType,
    type TaskForType,
} from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { getProcessRun, launchProcessRun, saveProcessDiagramPositions, updateProcessRun } from 'api/apiTask';
import { isObjectBoundTaskFor, resolveTaskFor } from '../../NewTask/helpers/initNewTaskStruct';
import {
    bindingEntityLabel,
    buildBindingHistoryComment,
    type BindingHistoryLabels,
} from '../../shared/helpers/taskBindingHelpers';

interface UseOffProcessRunParams {
    show: boolean;
    processId: number | null;
    onHide: () => void;
    onSaved: () => void;
}

const launchError = (code: string | undefined, t: (key: string) => string): string => {
    if (code === 'ROOT_DUE_REQUIRED') return t('processRuns.errRootDue');
    if (code === 'EMPTY_TYPE') return t('processRuns.errEmptyType');
    if (code === 'NO_ROOT') return t('processRuns.errNoRoot');
    if (code === 'INVALID_TASKS') return t('processRuns.errTasks');
    if (code === 'EXECUTOR_TIME_BUSY') return t('processRuns.errBusy');
    if (code === 'NOT_DRAFT') return t('processRuns.errNotDraft');
    return t('common:messError');
};

const leadFromRun = (run: Pick<ProcessRunType, 'leadInfo'>): LeadType | null => {
    if (!run.leadInfo?.ID) return null;
    return {
        ID: run.leadInfo.ID,
        clientName: run.leadInfo.clientName || '',
        clientPhone: run.leadInfo.clientPhone || '',
        leadStatus: Number(run.leadInfo.leadStatus || 0),
    } as LeadType;
};

const objectFromRun = (run: Pick<ProcessRunType, 'objectInfo'>): ObjectType | null => {
    if (!run.objectInfo?.ID) return null;
    return {
        ID: run.objectInfo.ID,
        objectName: run.objectInfo.objectName || '',
        clientName: run.objectInfo.clientName || '',
        onlySale: run.objectInfo.onlySale,
        onlyService: run.objectInfo.onlyService,
    } as ObjectType;
};

const bindingEntityName = (taskFor: TaskForType, lead: LeadType | null, object: ObjectType | null): string => {
    if (taskFor === 'lead') return String(lead?.clientName || '');
    if (isObjectBoundTaskFor(taskFor)) return String(object?.objectName || object?.clientName || '');
    return '';
};

export const useOffProcessRun = ({ show, processId, onHide, onSaved }: UseOffProcessRunParams) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [taskFor, setTaskFor] = useState<TaskForType>('personal');
    const [lead, setLead] = useState<LeadType | null>(null);
    const [object, setObject] = useState<ObjectType | null>(null);
    const [savedLeadId, setSavedLeadId] = useState<number | null>(null);
    const [savedObjectId, setSavedObjectId] = useState<number | null>(null);
    const [savedLead, setSavedLead] = useState<LeadType | null>(null);
    const [savedObject, setSavedObject] = useState<ObjectType | null>(null);
    const [tasks, setTasks] = useState<ProcessRunTaskType[]>([]);
    const [status, setStatus] = useState<'draft' | 'active' | 'done'>('draft');
    const [progressDone, setProgressDone] = useState(0);
    const [progressTotal, setProgressTotal] = useState(0);
    const [showDescription, setShowDescription] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [launching, setLaunching] = useState(false);
    const [sessionShow, setSessionShow] = useState(show);
    const [sessionId, setSessionId] = useState(processId);

    if (show !== sessionShow || processId !== sessionId) {
        setSessionShow(show);
        setSessionId(processId);
        setName('');
        setDescription('');
        setCategoryId(null);
        setTaskFor('personal');
        setLead(null);
        setObject(null);
        setSavedLeadId(null);
        setSavedObjectId(null);
        setSavedLead(null);
        setSavedObject(null);
        setTasks([]);
        setStatus('draft');
        setProgressDone(0);
        setProgressTotal(0);
        setShowDescription(false);
        setActiveTab('general');
        setSaving(false);
        setLaunching(false);
        setLoading(Boolean(show && processId));
    }

    useEffect(() => {
        if (!show || !processId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            const card = await getProcessRun(processId);
            if (cancelled) return;
            if (!card) {
                showToast(t('common:messError'), 'danger');
                setLoading(false);
                onHide();
                return;
            }
            const nextLead = leadFromRun(card);
            const nextObject = objectFromRun(card);
            const nextTaskFor = resolveTaskFor(
                card.leadId ? Number(card.leadId) : null,
                card.objectId ? Number(card.objectId) : null,
                nextObject,
            );
            setName(card.name || '');
            setDescription(card.description || '');
            setCategoryId(card.categoryId);
            setTaskFor(nextTaskFor);
            setLead(nextTaskFor === 'lead' ? nextLead : null);
            setObject(isObjectBoundTaskFor(nextTaskFor) ? nextObject : null);
            setSavedLeadId(nextTaskFor === 'lead' ? Number(card.leadId) || null : null);
            setSavedObjectId(isObjectBoundTaskFor(nextTaskFor) ? Number(card.objectId) || null : null);
            setSavedLead(nextTaskFor === 'lead' ? nextLead : null);
            setSavedObject(isObjectBoundTaskFor(nextTaskFor) ? nextObject : null);
            setTasks(card.tasks || []);
            setStatus(card.status);
            setProgressDone(card.progressDone || 0);
            setProgressTotal(card.progressTotal || 0);
            setShowDescription(Boolean(String(card.description || '').replace(/<[^>]*>/g, '').trim()));
            setLoading(false);
        };
        void load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, processId]);

    const nextLeadId = taskFor === 'lead' && lead?.ID ? Number(lead.ID) : null;
    const nextObjectId = isObjectBoundTaskFor(taskFor) && object?.ID ? Number(object.ID) : null;
    const bindingChanged =
        (nextLeadId || 0) !== (savedLeadId || 0) || (nextObjectId || 0) !== (savedObjectId || 0);

    const historyLabels = (): BindingHistoryLabels => ({
        personal: t('fields.personal'),
        lead: t('fields.lead'),
        object: t('fields.object'),
        sale: t('fields.sale'),
        service: t('fields.service'),
    });

    const payload = () => {
        const savedTaskFor = resolveTaskFor(savedLeadId, savedObjectId, savedObject);
        const data = {
            name: name.trim(),
            description,
            categoryId,
            tasks,
            leadId: nextLeadId,
            objectId: nextObjectId,
        };
        if (status === 'draft' || !bindingChanged) return data;
        const labels = historyLabels();
        return {
            ...data,
            historyComment: buildBindingHistoryComment(
                bindingEntityLabel(savedTaskFor, bindingEntityName(savedTaskFor, savedLead, savedObject), labels),
                bindingEntityLabel(taskFor, bindingEntityName(taskFor, lead, object), labels),
            ),
        };
    };

    const rejectInvalidBinding = () => {
        if (taskFor === 'lead' && !lead?.ID) {
            showToast(t('errEmptyLead'), 'danger');
            setActiveTab('general');
            return true;
        }
        if (isObjectBoundTaskFor(taskFor) && !object?.ID) {
            const errKey =
                taskFor === 'sale' ? 'errEmptySale' : taskFor === 'service' ? 'errEmptyService' : 'errEmptyObject';
            showToast(t(errKey), 'danger');
            setActiveTab('general');
            return true;
        }
        return false;
    };

    const reload = useCallback(async () => {
        if (!processId) return;
        const card = await getProcessRun(processId);
        if (!card) return;
        setTasks(card.tasks || []);
        setStatus(card.status);
        setProgressDone(card.progressDone || 0);
        setProgressTotal(card.progressTotal || 0);
    }, [processId]);

    const rejectInvalidName = () => {
        const trimmed = name.trim();
        if (!trimmed) {
            showToast(t('processDir.errEmptyName'), 'danger');
            setActiveTab('general');
            return true;
        }
        if (trimmed.length > PROCESS_TEMPLATE_NAME_MAX_LENGTH) {
            showToast(t('processDir.errNameTooLong'), 'danger');
            setActiveTab('general');
            return true;
        }
        return false;
    };

    const saveDraft = async (nextTasks: ProcessRunTaskType[] = tasks) => {
        if (!processId || status === 'done') return false;
        if (rejectInvalidBinding()) return false;
        const body = { ...payload(), tasks: nextTasks };
        const result = await updateProcessRun(processId, body);
        if (!result?.stat) {
            showToast(launchError(result?.errCode, t), 'danger');
            return false;
        }
        setSavedLeadId(body.leadId);
        setSavedObjectId(body.objectId);
        setSavedLead(taskFor === 'lead' ? lead : null);
        setSavedObject(isObjectBoundTaskFor(taskFor) ? object : null);
        return true;
    };

    const handleSave = async () => {
        if (!processId || status === 'done' || rejectInvalidName()) return;
        setSaving(true);
        const saved = await saveDraft();
        setSaving(false);
        if (!saved) return;
        showToast(t('processRuns.messSaved'), 'success');
        onSaved();
        onHide();
    };

    const saveDiagramPositions = async (positions: ProcessDiagramPositionUpdateType[]) => {
        if (!processId || status === 'draft') return false;
        const result = await saveProcessDiagramPositions(processId, { positions });
        if (!result?.stat || !result.process) {
            showToast(t('common:messError'), 'danger');
            return false;
        }
        setTasks(result.process.tasks || []);
        setStatus(result.process.status);
        setProgressDone(result.process.progressDone || 0);
        setProgressTotal(result.process.progressTotal || 0);
        showToast(t('processRuns.messSaved'), 'success');
        onSaved();
        return true;
    };

    const persistTasks = async (nextTasks: ProcessRunTaskType[]) => {
        if (!processId || status !== 'draft' || rejectInvalidName()) return false;
        setSaving(true);
        const saved = await saveDraft(nextTasks);
        setSaving(false);
        if (saved) onSaved();
        return saved;
    };

    const handleLaunch = async () => {
        if (!processId || status === 'done' || rejectInvalidName()) return;
        setLaunching(true);
        const saved = await saveDraft();
        if (!saved) {
            setLaunching(false);
            return;
        }
        const result = await launchProcessRun(processId);
        setLaunching(false);
        if (!result?.stat || !result.process) {
            showToast(launchError(result?.errCode, t), 'danger');
            return;
        }
        setStatus(result.process.status);
        setTasks(result.process.tasks || []);
        setProgressDone(result.process.progressDone || 0);
        setProgressTotal(result.process.progressTotal || 0);
        showToast(t('processRuns.messLaunched'), 'success');
        onSaved();
    };

    return {
        name,
        setName,
        description,
        setDescription,
        categoryId,
        setCategoryId,
        taskFor,
        setTaskFor,
        lead,
        setLead,
        object,
        setObject,
        tasks,
        setTasks,
        status,
        progressDone,
        progressTotal,
        showDescription,
        setShowDescription,
        activeTab,
        setActiveTab,
        loading,
        saving,
        launching,
        readOnly: status !== 'draft',
        locked: status === 'done',
        handleSave,
        handleLaunch,
        persistTasks,
        saveDiagramPositions,
        reload,
    };
};
